import React, { useState } from 'react'
import { Education } from '@/types'
import PortableText from '@/components/ui/PortableText'
import { SlideIn } from '@/components/animations/SlideIn'
import { FadeIn } from '@/components/animations/FadeIn'

interface EducationSectionProps {
  education: Education[]
}

const EducationSection: React.FC<EducationSectionProps> = ({ education }) => {
  const [filter, setFilter] = useState<'all' | 'blockchain'>('all')

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    })
  }

  const formatDateRange = (startDate: string, endDate: string | null) => {
    const start = formatDate(startDate)
    const end = endDate ? formatDate(endDate) : 'Present'
    return `${start} - ${end}`
  }

  const filteredEducation =
    filter === 'all' ? education : education.filter(edu => edu.isBlockchainRelated)

  return (
    <div className="space-y-6">
      {/* Filter buttons */}
      <FadeIn>
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-md shadow-sm bg-white dark:bg-gray-800 p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === 'all'
                  ? 'bg-primary text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              All Education
            </button>
            <button
              onClick={() => setFilter('blockchain')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === 'blockchain'
                  ? 'bg-primary text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Blockchain Only
            </button>
          </div>
        </div>
      </FadeIn>

      {filteredEducation.length > 0 ? (
        <div className="space-y-6">
          {filteredEducation.map((edu, index) => (
            <SlideIn key={edu._id} delay={index * 0.1}>
              <div
                className={`rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300 ${
                  edu.isBlockchainRelated
                    ? 'bg-gradient-to-br from-white to-secondary/5 dark:from-gray-800 dark:to-secondary/20 border border-secondary/30'
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                }`}
              >
                {edu.isBlockchainRelated && (
                  <div className="flex justify-end mb-2">
                    <span className="px-3 py-1 bg-secondary/20 text-secondary dark:text-secondary-light rounded-full text-xs font-medium">
                      Blockchain
                    </span>
                  </div>
                )}

                <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {edu.degree}
                    </h3>
                    <h4 className="text-lg font-semibold text-primary-light dark:text-primary-light mb-1">
                      {edu.institution}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">{edu.fieldOfStudy}</p>

                    {edu.location && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        <span className="inline-block mr-2">📍</span>
                        {edu.location}
                      </p>
                    )}

                    {/* Description */}
                    {edu.description && edu.description.length > 0 && (
                      <div className="prose prose-sm dark:prose-invert max-w-none mb-4">
                        <PortableText content={edu.description} />
                      </div>
                    )}

                    {/* Skills */}
                    {edu.skills && edu.skills.length > 0 && (
                      <div className="mt-4">
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                          Skills Acquired:
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {edu.skills.map(skill => (
                            <span
                              key={skill._id}
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                skill.category === 'blockchain'
                                  ? 'bg-secondary/20 text-secondary dark:text-secondary-light'
                                  : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                              }`}
                            >
                              {skill.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Certificate Link */}
                    {edu.certificateUrl && (
                      <div className="mt-4">
                        <a
                          href={edu.certificateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary-dark transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V7a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          View Certificate
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 md:mt-0 md:ml-6 flex-shrink-0">
                    <div
                      className={`px-4 py-2 rounded-lg ${
                        edu.isBlockchainRelated
                          ? 'bg-secondary/20 text-secondary dark:text-secondary-light'
                          : 'bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200'
                      }`}
                    >
                      <div className="text-sm font-medium">
                        {formatDateRange(edu.startDate, edu.endDate)}
                      </div>
                    </div>

                    {edu.grade && (
                      <div className="mt-2 text-center">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {edu.grade}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </SlideIn>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">
            No education information matches the selected filter.
          </p>
        </div>
      )}
    </div>
  )
}

export default EducationSection
